import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      toast.error('Invalid invitation link');
      navigate('/login');
    }
  }, [token, navigate]);

  const { data: invitation, isLoading, isError } = useQuery({
    queryKey: ['invitation-decode', token],
    queryFn: async () => {
      const { data } = await apiClient.get(`/auth/invitation?token=${encodeURIComponent(token)}`);
      return data.data as { email: string; tenantSlug: string };
    },
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/auth/accept-invitation', { token, password });
      if (!data.data) throw new Error(data.errors?.[0]?.message ?? 'Failed to activate account');
      return data.data as {
        accessToken: string;
        user: { id: string; email: string; displayName: string; userType: string; roles: string[]; tenantId: string };
      };
    },
    onSuccess: (data) => {
      login(data.accessToken, {
        ...data.user,
        lastLoginAt: null,
        entitledModules: [],
      });
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    },
    onError: (err: any) => {
      // 403 enrollment gate
      const enrollData = err?.response?.data?.data;
      if (enrollData?.mfaEnrollmentRequired) {
        navigate('/auth/mfa-enrollment', { state: { setupToken: enrollData.setupToken } });
        return;
      }
      toast.error(err.message || 'Failed to activate account');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (password.length < 12) { toast.error('Password must be at least 12 characters'); return; }
    acceptMutation.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-10 w-auto" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-text-primary font-display">VeriGRC</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Account Activation</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <CheckCircle2 className="h-10 w-10 text-accent-emerald" />
              <p className="text-base font-medium text-text-primary">Account activated!</p>
              <p className="text-sm text-text-secondary">Redirecting to your dashboard…</p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Verifying invitation…</span>
            </div>
          ) : isError ? (
            <div className="py-6 text-center space-y-2">
              <p className="text-sm font-medium text-accent-rose">Invalid or expired invitation link.</p>
              <p className="text-xs text-text-secondary">Please request a new invitation from your administrator.</p>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-text-primary">Set your password</h2>
                <p className="mt-0.5 text-sm text-text-secondary">Create a password to activate your account</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={invitation?.email ?? ''} readOnly className="opacity-70 cursor-not-allowed" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org">Organisation</Label>
                  <Input id="org" type="text" value={invitation?.tenantSlug ?? ''} readOnly className="opacity-70 cursor-not-allowed" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 12 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary focus-visible:outline-none"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={acceptMutation.isPending || !password || !confirm}
                >
                  {acceptMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Activating…</>
                  ) : (
                    'Activate Account'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
